import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import AppShell from "@/components/shell/AppShell";
import SignIn from "@/pages/auth/SignIn";
import SsoRedirect from "@/pages/auth/SsoRedirect";
import Dashboard from "@/pages/dashboard/Dashboard";
import OpportunityRadar from "@/pages/opportunities/OpportunityRadar";
import DeploymentWorkflow from "@/pages/deployment/DeploymentWorkflow";
import AgentInventory from "@/pages/agents/AgentInventory";
import AgentDetail from "@/pages/agents/AgentDetail";
import PolicyCatalogue from "@/pages/policies/PolicyCatalogue";
import OversightConsole from "@/pages/oversight/OversightConsole";
import IncidentRegister from "@/pages/incidents/IncidentRegister";
import ModelPerformance from "@/pages/measure/ModelPerformance";
import CostOptimisation from "@/pages/measure/CostOptimisation";
import Benchmarking from "@/pages/measure/Benchmarking";
import IntegrationHub from "@/pages/vendors/IntegrationHub";
import VendorPerformance from "@/pages/vendors/VendorPerformance";
import ReportPacks from "@/pages/reports/ReportPacks";
import ReportBuilder from "@/pages/reports/ReportBuilder";
import ScheduledReports from "@/pages/reports/ScheduledReports";
import ReportLibrary from "@/pages/reports/ReportLibrary";
import UsersAndRoles from "@/pages/settings/UsersAndRoles";
import ComplianceCentre from "@/pages/compliance/ComplianceCentre";
import AuditLog from "@/pages/audit/AuditLog";
import CommercialSummary from "@/pages/commercial/CommercialSummary";
import PlaceholderModule from "@/pages/PlaceholderModule";
import RouteGuard from "@/components/shell/RouteGuard";
import { PLACEHOLDER_ROUTES } from "./placeholderRoutes";

// Pre-filter routes that already have a concrete page so the map runs
// once at module load — not on every render.
const ACTIVE_ROUTES = new Set([
  "/identify/opportunities",
  "/deploy/requests",
  "/govern/agents",
  "/govern/policies",
  "/govern/oversight",
  "/govern/incidents",
  "/measure/performance",
  "/measure/cost",
  "/measure/benchmark",
  "/vendors/integrations",
  "/vendors/performance",
  "/reports/packs",
  "/reports/builder",
  "/reports/scheduled",
  "/reports/library",
  "/settings/users",
  "/commercial",
  "/compliance",
  "/settings/audit",
]);
const RESOLVED_PLACEHOLDERS = PLACEHOLDER_ROUTES.filter(
  (r) => !ACTIVE_ROUTES.has(r.path),
);

/**
 * Top-level route table.
 *
 * Public:
 *   /sign-in         · sign-in screen
 *   /auth/redirect   · SSO bridge
 *
 * Authenticated (rendered inside AppShell):
 *   /                · → /dashboard
 *   /dashboard       · role-based executive dashboard
 *   …                · IA placeholders for future waves
 */
export default function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/sign-in" element={<SignIn />} />
      <Route path="/auth/redirect" element={<SsoRedirect />} />

      {/* Authenticated shell */}
      <Route element={<AppShell />}>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route
          path="/identify/opportunities"
          element={
            <RouteGuard surface="identify" label="Opportunity Radar">
              <OpportunityRadar />
            </RouteGuard>
          }
        />
        <Route
          path="/deploy/requests"
          element={
            <RouteGuard surface="deploy" label="Deployment Workflow">
              <DeploymentWorkflow />
            </RouteGuard>
          }
        />
        <Route
          path="/govern/agents"
          element={
            <RouteGuard surface="govern.agents" label="Agent Inventory">
              <AgentInventory />
            </RouteGuard>
          }
        />
        <Route
          path="/govern/agents/:id"
          element={
            <RouteGuard surface="govern.agents" label="Agent Detail">
              <AgentDetail />
            </RouteGuard>
          }
        />
        <Route
          path="/govern/policies"
          element={
            <RouteGuard surface="govern.policies" label="Policy Catalogue">
              <PolicyCatalogue />
            </RouteGuard>
          }
        />
        <Route
          path="/govern/oversight"
          element={
            <RouteGuard surface="govern.oversight" label="Human-in-the-Loop Console">
              <OversightConsole />
            </RouteGuard>
          }
        />
        <Route
          path="/govern/incidents"
          element={
            <RouteGuard surface="govern.incidents" label="Incident Register">
              <IncidentRegister />
            </RouteGuard>
          }
        />
        <Route
          path="/measure/performance"
          element={
            <RouteGuard surface="measure" label="Model Performance & Drift">
              <ModelPerformance />
            </RouteGuard>
          }
        />
        <Route
          path="/measure/cost"
          element={
            <RouteGuard surface="measure" label="Cost Optimisation">
              <CostOptimisation />
            </RouteGuard>
          }
        />
        <Route
          path="/measure/benchmark"
          element={
            <RouteGuard surface="measure" label="Benchmarking">
              <Benchmarking />
            </RouteGuard>
          }
        />
        <Route
          path="/vendors/integrations"
          element={
            <RouteGuard surface="vendors" label="Integration Hub">
              <IntegrationHub />
            </RouteGuard>
          }
        />
        <Route
          path="/vendors/performance"
          element={
            <RouteGuard surface="vendors" label="Vendor Performance">
              <VendorPerformance />
            </RouteGuard>
          }
        />
        <Route
          path="/reports/packs"
          element={
            <RouteGuard surface="reports" label="Pre-Built Packs">
              <ReportPacks />
            </RouteGuard>
          }
        />
        <Route
          path="/reports/builder"
          element={
            <RouteGuard surface="reports" label="Custom Report Builder">
              <ReportBuilder />
            </RouteGuard>
          }
        />
        <Route
          path="/reports/scheduled"
          element={
            <RouteGuard surface="reports" label="Scheduled Reports">
              <ScheduledReports />
            </RouteGuard>
          }
        />
        <Route
          path="/reports/library"
          element={
            <RouteGuard surface="reports" label="Reports Library">
              <ReportLibrary />
            </RouteGuard>
          }
        />
        <Route
          path="/settings/users"
          element={
            <RouteGuard surface="settings.users" label="Users & Roles">
              <UsersAndRoles />
            </RouteGuard>
          }
        />
        <Route
          path="/compliance"
          element={
            <RouteGuard surface="compliance" label="Compliance Centre">
              <ComplianceCentre />
            </RouteGuard>
          }
        />
        <Route
          path="/settings/audit"
          element={
            <RouteGuard surface="settings.audit" label="Audit Log">
              <AuditLog />
            </RouteGuard>
          }
        />
        <Route
          path="/commercial"
          element={
            <RouteGuard surface="commercial" label="Commercial Summary">
              <CommercialSummary />
            </RouteGuard>
          }
        />

        {/* Placeholders — future waves */}
        {RESOLVED_PLACEHOLDERS.map((r) => (
          <Route
            key={r.path}
            path={r.path}
            element={
              <PlaceholderModule
                eyebrow={r.eyebrow}
                title={r.title}
                description={r.description}
              />
            }
          />
        ))}
      </Route>

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
